// Types + fetch for the admin Orders screen (#258, ADR 0060).
//
// `GET /api/admin/orders*` on web-bff answers two different shapes
// depending on which backend served the request — see the ADR. A
// `customer` filter switches the source entirely rather than the two being
// merged, so the list response is a discriminated union on `source`.

export const ORDER_STAGES = ["payment", "inventory", "shipping", "fraud"] as const;
export type OrderStageName = (typeof ORDER_STAGES)[number];

export interface OrderStage {
  result?: "processed" | "failed";
  reason?: string;
  at?: string;
}

/** order-status-service's per-order projection — fast, has status/stages, no $. */
export interface OrderStatus {
  orderId: string;
  status: "in_progress" | "fulfilled" | "failed";
  failedStage?: OrderStageName;
  stages: Partial<Record<OrderStageName, OrderStage>>;
  placedAt?: string;
  updatedAt: string;
}

export interface OrderLine {
  sku: string;
  quantity: number;
  unitPrice: number;
}

/** order-service's SoR record — authoritative, has $/line items, no real status. */
export interface OrderRecord {
  orderId: string;
  customerId: string;
  customerTier?: string;
  items: OrderLine[];
  total: number;
  currency: string;
  status: string; // always "PLACED" — order-service never learns fulfilment progress
  placedAt: string;
  outboxStatus: "PENDING" | "DISPATCHED";
}

export const ORDER_STATUS_FILTERS = ["all", "in_progress", "fulfilled", "failed"] as const;
export type OrderStatusFilter = (typeof ORDER_STATUS_FILTERS)[number];

export type OrdersListResult =
  | { kind: "status-list"; source: "order-status-service"; count: number; orders: OrderStatus[] }
  | { kind: "customer-list"; source: "order-service"; count: number; orders: OrderRecord[] }
  | { kind: "unavailable"; status: number; error: string };

export interface OrdersFilter {
  status?: OrderStatusFilter;
  sinceHours?: number;
  customer?: string;
  limit?: number;
}

/** `GET /api/admin/orders[?status=&since=&customer=&limit=]`. A non-empty
 * `customer` switches the backend (and drops status/since — order-service
 * doesn't support them) rather than the two being combined. */
export async function fetchOrders(filter: OrdersFilter): Promise<OrdersListResult> {
  const params = new URLSearchParams();
  const customer = filter.customer?.trim();
  if (customer) {
    params.set("customer", customer);
  } else {
    if (filter.status && filter.status !== "all") {
      params.set("status", filter.status);
    }
    if (filter.sinceHours && filter.sinceHours > 0) {
      params.set("since", new Date(Date.now() - filter.sinceHours * 3_600_000).toISOString());
    }
  }
  if (filter.limit && filter.limit > 0) {
    params.set("limit", String(filter.limit));
  }

  let res: Response;
  try {
    res = await fetch(`/api/admin/orders?${params}`, { credentials: "include" });
  } catch (err) {
    return unavailable(0, err);
  }

  const body = await safeJson(res);
  if (res.status === 200 && body && typeof body === "object") {
    const b = body as { count: number; orders: unknown[]; source?: string };
    return b.source === "order-service"
      ? { kind: "customer-list", source: "order-service", count: b.count, orders: b.orders as OrderRecord[] }
      : { kind: "status-list", source: "order-status-service", count: b.count, orders: b.orders as OrderStatus[] };
  }

  return unavailable(res.status, undefined, body);
}

export interface OrderDetail {
  order: OrderRecord;
  status: OrderStatus | null;
}

export type OrderDetailResult =
  | { kind: "detail"; body: OrderDetail }
  | { kind: "not_found" }
  | { kind: "unavailable"; status: number; error: string };

/** `GET /api/admin/orders/{orderId}` — order-service's record plus
 * order-status-service's projection (null when the read model hasn't
 * caught up, is disabled, or the invoke failed — best-effort). */
export async function fetchOrderDetail(orderId: string): Promise<OrderDetailResult> {
  let res: Response;
  try {
    res = await fetch(`/api/admin/orders/${encodeURIComponent(orderId)}`, { credentials: "include" });
  } catch (err) {
    return unavailable(0, err);
  }

  if (res.status === 404) {
    return { kind: "not_found" };
  }

  const body = await safeJson(res);
  if (res.status === 200 && body) {
    return { kind: "detail", body: body as OrderDetail };
  }

  return unavailable(res.status, undefined, body);
}

async function safeJson(res: Response): Promise<unknown> {
  try {
    return await res.json();
  } catch {
    return null;
  }
}

function unavailable(status: number, err?: unknown, body?: unknown): { kind: "unavailable"; status: number; error: string } {
  const fromBody = body && typeof body === "object" ? (body as { error?: string }).error : undefined;
  const error =
    fromBody ?? (err instanceof Error ? err.message : status === 0 ? "request failed (network)" : `status ${status}`);
  return { kind: "unavailable", status, error };
}
